<!-- View for admins to search for a sales contact to impersonate -->
<template>
	<div
		class="h-full overflow-auto"
		@scroll="handleScroll"
	>
		<HeaderNav />
		<div
			class="mx-auto mt-16 flex w-full max-w-177.5 flex-col items-center pt-16"
		>
			<img
				src="/assets/logos/studio_name_mark_gradient.svg"
				alt="Sales Studio logo"
				class="mt-4 w-74.5"
			/>

			<!-- Search input -->
			<div class="relative mt-16 flex w-full flex-col">
				<input
					class="text-secondary-900 placeholder:text-secondary-400 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-slate-500 focus:outline-hidden"
					type="text"
					placeholder="AFP’s first name, last name, or Hub email address"
					autocomplete="off"
					name="search term"
					v-model="searchTerm"
					autofocus
				/>
				<img
					v-if="loading"
					:src="spinner"
					alt="Loading spinner"
					class="absolute top-3 right-3 size-5! animate-spin"
				/>
			</div>

			<!-- Error message -->
			<BaseAlert
				type="critical"
				class="mt-16"
				v-if="error"
			>
				{{ error }}
				<button
					type="button"
					class="ml-2 rounded-xs border border-white p-1 text-xs font-normal text-white uppercase hover:bg-white hover:text-red-700"
					@click="search"
				>
					{{ t('general.retry') }}
				</button>
			</BaseAlert>

			<!-- Results -->
			<div class="relative mt-3 mb-16 w-full divide-y divide-slate-100">
				<SalesContactListItem
					v-for="contact in results"
					:key="contact.id"
					:email="contact.email"
					:firstname="contact.firstName"
					:lastname="contact.lastName"
					:id="contact.id"
				/>
				<div
					v-if="loading && searchPage !== 1"
					class="mt-4 flex justify-center"
				>
					<img
						:src="spinner"
						alt="Loading spinner"
						class="size-5! animate-spin"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import HeaderNav from '../components/common/HeaderNav.vue'
import type { V3SalesContact, V4SalesContact } from '@/apiTypes'
import spinner from '@/assets/spinner.svg'
import BaseAlert from '@/components/common/BaseAlert.vue'
import SalesContactListItem from '@/components/login/SalesContactListItem.vue'
import { axiosInstance } from '@/services/axiosInstance'
import { Feature, useFeatureService } from '@/services/featureService'
import type { Paginated, SalesContactListItemResult } from '@/types'
import { captureException } from '@sentry/vue'
import { watchDebounced } from '@vueuse/core'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const searchTerm = ref('')
const loading = ref(false)
const results = ref<SalesContactListItemResult[]>([])
const error = ref('')
const isUsingSfIntegration = ref(
	useFeatureService().isEnabled(Feature.SalesforceIntegration)
)
const searchPage = ref(1)
const noMoreResults = ref(false)

type ApiSalesContact = {
	data: V3SalesContact[]
}

/** Search for sales contacts matching the search term */
async function search() {
	if (searchTerm.value === '') {
		results.value = []
		loading.value = false
		return
	}

	searchPage.value = 1
	loading.value = true
	const searchText = searchTerm.value

	isUsingSfIntegration.value
		? await salesforceSearch(searchText)
		: await crmSearch(searchText)
}

/** Query old CRM for AFPs based on user input */
async function crmSearch(searchText: string) {
	try {
		results.value = []
		const response = await axiosInstance.get<ApiSalesContact>(
			`/v3/counselors/search?q=${searchText}`
		)

		response.data.data.forEach((contact) => {
			results.value.push({
				id: contact.id,
				email: contact.email,
				firstName: contact.firstname,
				lastName: contact.lastname,
			})
		})
		if (searchText === searchTerm.value) {
			loading.value = false
		}
	} catch (e: any) {
		console.error(e)
		captureException(e)
		error.value = e.message
		loading.value = false
	}
}

/** Query for AFPs based on user input */
async function salesforceSearch(searchText: string) {
	const scopeList = getScopes()

	// check for current page; if 1 we need to clear previous results
	if (searchPage.value === 1) {
		results.value = []
	}

	try {
		const include: string[] = []

		const runSearch = async (scopes: string[]) => {
			const response = await axiosInstance.get<Paginated<V4SalesContact>>(
				'/v4/contacts/workers?scope=isAfp|' +
					scopes.join('|') +
					'&include=' +
					include.join() +
					'&page=' +
					searchPage.value
			)

			if (response.data.meta.lastPage === searchPage.value) {
				noMoreResults.value = true
			}

			return response.data.data
		}

		// run first name search
		let searchResults = await runSearch(scopeList[0])

		// fallback to last name search if the results for first name are empty
		if (scopeList.length > 1 && searchResults.length === 0) {
			searchResults = await runSearch(scopeList[1])
		}

		searchResults.forEach((contact) => {
			results.value.push({
				id: contact.id,
				email: contact.email,
				firstName: contact.firstName,
				lastName: contact.lastName,
			})
		})

		if (searchText === searchTerm.value) {
			loading.value = false
		}
	} catch (e: any) {
		console.error(e)
		captureException(e)
		error.value = e.message
		loading.value = false
	}
}

/** Convert search query into combined firstName / lastName scopes */
const getScopes = (): string[][] => {
	const query = searchTerm.value

	// Split input into words (e.g. "kelly townsend" into ["kelly", "townsend"])
	const parts = query.split(/\s+/)

	// For a single word search try firstName first, fallback to lastName
	// This allows us to simulate an "OR" search while the API uses AND logic
	if (parts.length === 1) {
		return [['firstName::' + parts[0]], ['lastName::' + parts[0]]]
	}

	// For multi-word search assume user entered "firstName lastName"
	// This will be treated as an AND search by the API
	return [['firstName::' + parts[0], 'lastName::' + parts[1]]]
}

/**
 * Scroll event handler to trigger infinite loading
 * Use a small buffer (40px) so we trigger before hitting the exact bottom.
 * Using scrollheight without the buffer is unreliable due to quick scroll / dynamic content.
 * @param  {event} e Scroll event
 */
const handleScroll = (e: Event) => {
	const el = e.currentTarget as HTMLElement | null

	if (!el) {
		return
	}

	const bottomOfWindow = el.scrollTop + el.offsetHeight > el.scrollHeight - 40

	if (bottomOfWindow && !noMoreResults.value) {
		searchMoreLeads(searchPage.value + 1)
	}
}

const searchMoreLeads = (page: number) => {
	if (loading.value) {
		return
	}
	loading.value = true
	searchPage.value = page
	salesforceSearch(searchTerm.value)
}

/** Execute the search when the searchTerm changes */
watchDebounced(searchTerm, search, { debounce: 500, maxWait: 2000 })
</script>
